import {
  backdropColors,
  colorHarmonies,
  contrastLevel,
  contrastRatio,
  isLargeText,
  opaqueHex,
  smallestFontSize,
} from "@cva/shared";
import { useMemo } from "react";
import { useImageColors } from "../lib/palette";
import { cn } from "../lib/utils";
import { Swatch } from "./ColorPicker";
import { useEditor } from "./store";

/**
 * Live contrast of a text colour to what lies beneath the selected text
 * (WCAG 2): shown while the colour being edited is the text's colour.
 */
function ContrastHint({ value }: { value: string }) {
  const doc = useEditor((s) => s.doc);
  const selection = useEditor((s) => s.selection);
  const element = selection.length === 1 ? doc.elements[selection[0]!] : undefined;
  const text = element?.type === "text" ? element : undefined;
  const backdrop = useMemo(() => (text ? backdropColors(doc, text.id) : null), [doc, text]);
  if (!text || opaqueHex(text.style.color) !== opaqueHex(value)) return null;
  if (!backdrop) {
    return (
      <p className="rounded-md bg-zinc-50 px-2 py-1.5 text-[11px] text-zinc-500">
        Kontrast nicht prüfbar – der Text liegt auf einem Bild oder einer Grafik.
      </p>
    );
  }
  const ratio = Math.min(...backdrop.map((bg) => contrastRatio(opaqueHex(value), bg)));
  const large = isLargeText(doc, smallestFontSize(text), text.style.fontWeight);
  const required = large ? 3 : 4.5;
  const level = contrastLevel(ratio);
  const ok = ratio >= required;
  return (
    <div
      className={cn(
        "flex items-center gap-2 rounded-md px-2 py-1.5 text-[11px]",
        ok ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-800",
      )}
      title={`WCAG AA verlangt ${required.toString().replace(".", ",")} : 1${large ? " (große Schrift)" : ""}`}
    >
      <span
        className="size-4 shrink-0 rounded ring-1 ring-black/10 ring-inset"
        style={{ background: backdrop[0] }}
      />
      <span className="flex-1">
        Kontrast zum Hintergrund: <b>{ratio.toFixed(1).replace(".", ",")} : 1</b>
      </span>
      <span
        className={cn(
          "rounded px-1.5 py-0.5 font-semibold",
          ok ? "bg-emerald-600 text-white" : "bg-red-600 text-white",
        )}
      >
        {ok ? (level === "AA groß" ? "AA" : level) : "zu gering"}
      </span>
    </div>
  );
}

/**
 * Extra rows of the colour editor: colours taken from images ("Aus Bild")
 * and harmonies of the current colour (complementary, analogous, triadic).
 */
export function ColorSuggestions({
  value,
  onPick,
}: {
  value: string;
  onPick: (color: string) => void;
}) {
  const imageColors = useImageColors();
  const current = opaqueHex(value);
  const harmonies = colorHarmonies(current);
  // Greys (and black/white) have no hue to rotate.
  const chromatic = [...harmonies.complementary, ...harmonies.triadic].some(
    (color) => color !== current,
  );
  const rows: { label: string; colors: string[] }[] = [
    { label: "Komplementär", colors: harmonies.complementary },
    { label: "Analog", colors: harmonies.analogous },
    { label: "Triade", colors: harmonies.triadic },
  ];
  return (
    <>
      <ContrastHint value={value} />
      {imageColors.length > 0 ? (
        <div>
          <p className="mb-1.5 text-xs font-medium text-zinc-500">Aus Bild</p>
          <div className="flex flex-wrap gap-1.5">
            {imageColors.map((color) => (
              <Swatch key={color} color={color} onClick={() => onPick(color)} />
            ))}
          </div>
        </div>
      ) : null}
      {chromatic ? (
        <div>
          <p className="mb-1.5 text-xs font-medium text-zinc-500">Farbharmonien</p>
          <div className="flex flex-wrap items-center gap-3">
            {rows.map((row) => (
              <div key={row.label} className="flex items-center gap-1" title={row.label}>
                {row.colors.map((color) => (
                  <Swatch
                    key={color}
                    color={color}
                    title={`${row.label}: ${color}`}
                    onClick={() => onPick(color)}
                    className="size-5"
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </>
  );
}
